/**
 * MasterPushConfigDialog (iter495v)
 *
 * Grants schedulers granular control over the Customer Master push
 * pipeline used by Review & Publish. Config options:
 *   • Master global toggle (default ON)
 *   • Combined disable picker — pick clients OR sites to opt out
 *   • Distribution mode — Proportional / Equal split
 *   • Excluded services — never receive distribution
 *   • Round each per-line price to the nearest £1
 *
 * Consumed by the bulk-promote endpoint. Persists to
 * budget_master_push_config (one row per organisation).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Save, X, Settings, ShieldOff } from 'lucide-react';

import api from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';

export function MasterPushConfigDialog({ open, onClose, onSaved, clients = [], services = [] }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState({
    push_enabled: true,
    disabled_scopes: [],
    distribution_mode: 'proportional',
    excluded_service_ids: [],
    round_to_pound: false,
  });
  const [pickerQuery, setPickerQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api.get('/reports/revenue-budget/master-push-config')
      .then(r => { if (!cancelled) setCfg(r.data); })
      .catch(err => toast({ title: 'Failed to load config', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' }))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, toast]);

  // Flatten clients + their sites into a single searchable list.
  const scopeCandidates = useMemo(() => {
    const out = [];
    (clients || []).forEach(c => {
      out.push({ key: `client:${c.id}`, label: c.name, sub: 'Client', clientId: c.id });
      (c.sites || []).forEach(s => {
        out.push({ key: `site:${s.id}`, label: s.name, sub: c.name, siteId: s.id, clientId: c.id });
      });
    });
    return out;
  }, [clients]);
  const filteredScopes = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return scopeCandidates.slice(0, 40);
    return scopeCandidates.filter(x =>
      x.label.toLowerCase().includes(q) || (x.sub && x.sub.toLowerCase().includes(q))
    ).slice(0, 40);
  }, [scopeCandidates, pickerQuery]);

  const disabledSet = useMemo(() => new Set(cfg.disabled_scopes || []), [cfg.disabled_scopes]);
  const toggleScope = (key) => {
    setCfg(c => ({
      ...c,
      disabled_scopes: c.disabled_scopes.includes(key)
        ? c.disabled_scopes.filter(k => k !== key)
        : [...c.disabled_scopes, key],
    }));
  };
  const toggleService = (id) => {
    setCfg(c => ({
      ...c,
      excluded_service_ids: c.excluded_service_ids.includes(id)
        ? c.excluded_service_ids.filter(x => x !== id)
        : [...c.excluded_service_ids, id],
    }));
  };

  const commit = async () => {
    setSaving(true);
    try {
      const r = await api.patch('/reports/revenue-budget/master-push-config', cfg);
      setCfg(r.data);
      toast({ title: 'Master push settings saved' });
      onSaved && onSaved(r.data);
      onClose && onClose();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const scopeChips = (cfg.disabled_scopes || []).map(key => {
    const found = scopeCandidates.find(x => x.key === key);
    return { key, label: found ? found.label : key, sub: found?.sub };
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent
        className="dialog-resizable max-w-3xl"
        style={{ width: '860px', maxHeight: '85vh', overflowY: 'auto' }}
        data-testid="master-push-config-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Customer Master File Syncing
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading current settings…
          </div>
        ) : (
          <div className="space-y-5 py-2">
            {/* Global toggle */}
            <section className="rounded border border-slate-200 p-3 flex items-center justify-between gap-4 bg-slate-50/50">
              <div>
                <Label htmlFor="mp-global" className="text-sm font-semibold flex items-center gap-2">
                  <ShieldOff className="h-3.5 w-3.5" />
                  Master push updates
                </Label>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  When OFF, Review &amp; Publish will keep budget entries but never touch the Customer Master.
                </p>
              </div>
              <Switch
                id="mp-global"
                checked={!!cfg.push_enabled}
                onCheckedChange={(v) => setCfg(c => ({ ...c, push_enabled: !!v }))}
                data-testid="mp-push-enabled-switch"
              />
            </section>

            {/* Disabled scopes */}
            <section className="rounded border border-slate-200 p-3 space-y-2">
              <Label className="text-sm font-semibold">Opt-out list (clients &amp; sites)</Label>
              <p className="text-[11px] text-slate-500">
                Sites belonging to an opted-out client are automatically excluded. Picking a specific site opts out only that site.
              </p>
              {scopeChips.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="mp-opted-out-chips">
                  {scopeChips.map(chip => (
                    <button
                      key={chip.key}
                      onClick={() => toggleScope(chip.key)}
                      className="text-[11px] bg-rose-100 text-rose-900 border border-rose-200 rounded px-2 py-0.5 hover:bg-rose-200 flex items-center gap-1"
                      data-testid={`mp-chip-${chip.key}`}
                    >
                      <span className="uppercase text-[9px] font-bold text-rose-700">{chip.key.split(':')[0]}</span>
                      {chip.label}
                      <X className="h-3 w-3" />
                    </button>
                  ))}
                </div>
              )}
              <Input
                type="search"
                placeholder="Search clients or sites to opt out…"
                value={pickerQuery}
                onChange={(e) => setPickerQuery(e.target.value)}
                className="text-xs"
                data-testid="mp-scope-search"
              />
              <div className="max-h-40 overflow-y-auto border border-slate-200 rounded bg-white">
                {filteredScopes.length === 0 ? (
                  <div className="p-3 text-[11px] text-slate-400 text-center">No matches.</div>
                ) : (
                  filteredScopes.map(x => {
                    const checked = disabledSet.has(x.key);
                    return (
                      <label
                        key={x.key}
                        className="flex items-center gap-2 px-2 py-1.5 border-b border-slate-100 hover:bg-slate-50 cursor-pointer text-xs"
                        data-testid={`mp-scope-row-${x.key}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleScope(x.key)}
                        />
                        <span className={`uppercase text-[9px] font-bold px-1.5 py-0.5 rounded ${x.key.startsWith('client:') ? 'bg-slate-200 text-slate-700' : 'bg-emerald-100 text-emerald-800'}`}>
                          {x.key.split(':')[0]}
                        </span>
                        <span className="flex-1 truncate">{x.label}</span>
                        {x.sub && <span className="text-slate-400">{x.sub}</span>}
                      </label>
                    );
                  })
                )}
              </div>
            </section>

            {/* Distribution mode */}
            <section className="rounded border border-slate-200 p-3 space-y-3">
              <Label className="text-sm font-semibold">How to distribute a promoted price across service lines</Label>
              <div className="grid grid-cols-2 gap-2">
                <label className={`text-xs border rounded p-2 cursor-pointer ${cfg.distribution_mode === 'proportional' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'}`}
                       data-testid="mp-mode-proportional">
                  <input
                    type="radio"
                    checked={cfg.distribution_mode === 'proportional'}
                    onChange={() => setCfg(c => ({ ...c, distribution_mode: 'proportional' }))}
                    className="mr-2"
                  />
                  <b>Proportional</b>
                  <p className="text-[10px] text-slate-500 mt-0.5">Each line keeps its share of the total (default).</p>
                </label>
                <label className={`text-xs border rounded p-2 cursor-pointer ${cfg.distribution_mode === 'equal' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'}`}
                       data-testid="mp-mode-equal">
                  <input
                    type="radio"
                    checked={cfg.distribution_mode === 'equal'}
                    onChange={() => setCfg(c => ({ ...c, distribution_mode: 'equal' }))}
                    className="mr-2"
                  />
                  <b>Equal split</b>
                  <p className="text-[10px] text-slate-500 mt-0.5">Every included line receives the same amount.</p>
                </label>
              </div>

              {/* Excluded services */}
              <div>
                <Label className="text-xs font-semibold">Do NOT distribute to these service lines</Label>
                <p className="text-[10px] text-slate-500">Selected services keep their existing price when a master push runs.</p>
                <div className="mt-2 max-h-32 overflow-y-auto border border-slate-200 rounded bg-white">
                  {(services || []).length === 0 ? (
                    <div className="p-3 text-[11px] text-slate-400 text-center">No services available.</div>
                  ) : services.map(s => {
                    const excluded = (cfg.excluded_service_ids || []).includes(s.id);
                    return (
                      <label
                        key={s.id}
                        className="flex items-center gap-2 px-2 py-1.5 border-b border-slate-100 hover:bg-slate-50 cursor-pointer text-xs"
                        data-testid={`mp-service-row-${s.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={excluded}
                          onChange={() => toggleService(s.id)}
                        />
                        <span className="flex-1">{s.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Rounding */}
              <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-2 mt-2">
                <div>
                  <Label className="text-xs font-semibold">Round each per-line price to the nearest £1</Label>
                  <p className="text-[10px] text-slate-500">Any pence residual is absorbed by the last distributed line so the total still matches.</p>
                </div>
                <Switch
                  checked={!!cfg.round_to_pound}
                  onCheckedChange={(v) => setCfg(c => ({ ...c, round_to_pound: !!v }))}
                  data-testid="mp-round-switch"
                />
              </div>
            </section>
          </div>
        )}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="mp-cancel-btn">Cancel</Button>
          <Button onClick={commit} disabled={saving || loading} data-testid="mp-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Save settings
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default MasterPushConfigDialog;
